import { get, cloneDeep, isArray } from 'lodash';
import {
  Answers,
  IConsultation,
  IFlattenedQuestion,
  IQuestionModel,
  FlattenedQuestionType
} from '@care-portals/api-interfaces';
import { skinGoalMap } from './questions/Acne/Initial/acne';
import {
  ConditionQuestion,
  FormTypeQuestion,
  formTypes
} from './form-types';
import { QuestionsCatalog } from './questions';
import { calculateBMI, formatHeight } from './utils/bmi';
import { PATIENT_WEB_PAGES_URLS } from '../PATIENT_WEB_PAGES_URLS';
import { klona } from 'klona';

// TODO convert and move to utils
export class ConsultationUtils {
  static answers: Answers;
  static requiredQuestions: Array<any>;

  static findFromNode(
    node: any,
    qs: Array<number>,
    questionType: string,
    questionStack: string[]
  ) {
    if (typeof node === 'string' && node == questionType) {
      // Found it!
      return qs;
    }
    if (typeof node === 'object' && node.key === questionType) {
      return qs;
    }
    if (isArray(node)) {
      let skipped = 0;
      for (let i in node) {
        if (
          !isArray(node[i]) &&
          !questionStack.includes(this.getQuestionTypeFromNode(node[i]))
        ) {
          skipped++;
          continue;
        }
        qs.push(
          qs.length === 0 ? parseInt(i) - skipped + 1 : parseInt(i) - skipped
        );

        const type = this.findFromNode(
          node[i],
          qs,
          questionType,
          questionStack
        );
        if (type) {
          return type;
        }
        if (node.length > 0) {
          qs.pop();
        }
      }
    }
    return null;
  }

  static getCurrentQuestionStack(curQuestionType: string) {
    let head = this.getQuestionTypeFromNode(this.requiredQuestions);
    const questionStack = [head];
    while (head && head !== curQuestionType) {
      head = this.getNext(head);
      if (head) {
        questionStack.push(head);
      }
    }
    return questionStack;
  }

  static getIndexOfQuestion(questionType: string) {
    const stack = cloneDeep([...this.requiredQuestions]);
    const curQuestionStack = this.getCurrentQuestionStack(questionType);
    return this.findFromNode(stack, [], questionType, curQuestionStack);
  }

  static getNext(curQuestionType: string) {
    let stack = [],
      node;
    stack = cloneDeep([...this.requiredQuestions]);
    let currentNode;

    while (stack.length > 0) {
      node = stack.shift();
      if (typeof node === 'string' && node == curQuestionType) {
        // Found it!
        currentNode = node;
        break;
      }
      if (typeof node === 'object' && node.key === curQuestionType) {
        currentNode = node;
        break;
      }
      // if array
      if (typeof node === 'object' && node.length > 0) {
        stack = cloneDeep([...node, ...stack]);
      }
    }

    if (typeof currentNode === 'object') {
      if (currentNode.isLast === true) {
        return null;
      }
      return stack.length > 0 && currentNode.next
        ? this.getNextByCondition(currentNode.next, stack[0])
        : null;
    }

    if (typeof currentNode === 'string') {
      const question = QuestionsCatalog.find(q => q.field === currentNode);
      if (question?.isLast) {
        return null;
      }
      return stack.length > 0 ? this.getQuestionTypeFromNode(stack[0]) : null;
    }

    // Didn't find it. Return null.
    return null;
  }

  static checkCondition(condition) {
    let ok = true;

    Object.entries(condition).forEach(([k, conditionValue], i) => {
      let answerValue = get(this.answers, k, '');
      if (typeof conditionValue === 'boolean') {
        answerValue = !!answerValue;
      }
      if (!!conditionValue && typeof conditionValue === 'object') {
        if (
          conditionValue['$ne'] !== undefined &&
          answerValue === conditionValue['$ne']
        ) {
          ok = false;
        }
      } else if (answerValue !== conditionValue) {
        ok = false;
      }
    });

    return ok;
  }

  static getNextByCondition(nextObj, firstOfStack) {
    if (Object.keys(nextObj).length === 1 && nextObj.key) {
      return nextObj.key;
    }
    if (nextObj.key && nextObj.condition) {
      const { condition = {}, els = undefined, key = undefined } = nextObj;

      if (this.checkCondition(condition)) {
        return key;
      } else {
        if (els) {
          return els;
        }
        return this.getQuestionTypeFromNode(firstOfStack);
      }
    }
    if (nextObj.switch) {
      for (let i = 0; i < nextObj.switch.length; i++) {
        if (this.checkCondition(nextObj.switch[i].condition)) {
          return nextObj.switch[i].key;
        }
      }
      return nextObj.els;
    }

    return null;
  }

  static getQuestionTypeFromNode(node): string {
    let cloneNode = klona(node);

    while (typeof cloneNode === 'object') {
      if (cloneNode.length > 0) {
        cloneNode = cloneNode[0];
      } else {
        break;
      }
    }
    return typeof cloneNode === 'string' ? cloneNode : cloneNode.key;
  }

  static getQuestionStackFromAnswers(
    requiredQuestions,
    answers,
    includeEmptyAnswers = false
  ): Array<string> {
    this.requiredQuestions = requiredQuestions;
    this.answers = answers;
    const questionStack: string[] = [];
    let questionType = this.getQuestionTypeFromNode(requiredQuestions);
    let infiniteLoopCheck = 0;

    while (questionType) {
      if (infiniteLoopCheck > 1000) {
        console.error('Infinite loop detected', questionStack);
        if (typeof window !== undefined) {
          window.alert('Infinite loop detected, please contact support');
        }
        throw new Error('Infinite loop detected');
      }
      infiniteLoopCheck++;

      if (answers[questionType] || includeEmptyAnswers) {
        questionStack.push(questionType);
      } else {
        const questionModel = QuestionsCatalog.find(
          q => q.field == questionType
        );
        if (questionModel.type === 'file_upload') {
          // file_upload type does not store its answers with the question field name, we need to do this manually
          const doesQuestionHaveFilesUploaded = questionModel.answer?.some?.(
            a => !!answers[a.resourceId]
          );
          if (doesQuestionHaveFilesUploaded) {
            questionStack.push(questionType);
          }
        }

        if (questionModel.type === 'image_list') {
          // image_list type does not store its answers with the question field name, they store directly with a number suffix
          const resourceId = questionModel?.answer?.option?.resourceId;
          if (resourceId) {
            const doesQuestionHaveFilesUploaded = Object.keys(answers).some(
              key => key.startsWith(resourceId) && /\d$/.test(key)
            );
            if (doesQuestionHaveFilesUploaded) {
              questionStack.push(questionType);
            }
          }
        }
      }

      questionType = this.getNext(questionType);
    }
    return questionStack;
  }

  static getAllQuestions(requiredQuestions: FormTypeQuestion[]): string[] {
    const qsMap = {};

    const addQuestion = (qs: string) => {
      qsMap[qs] = true;
    };

    const addQuestionForObj = (obj: ConditionQuestion) => {
      if (obj.key) {
        addQuestion(obj.key);
      }
      if (obj.next) {
        if (obj.next.key) {
          addQuestion(obj.next.key);
        }
        if (obj.next.els) {
          addQuestion(obj.next.els);
        }
        if (obj.next.switch) {
          obj.next.switch.forEach(s => {
            addQuestion(s.key);
          });
        }
      }
    };

    requiredQuestions.forEach(_q => {
      if (typeof _q === 'string') {
        addQuestion(_q);
      } else if (Array.isArray(_q)) {
        _q.forEach(__q => {
          if (typeof __q === 'string') {
            addQuestion(__q);
          } else {
            addQuestionForObj(__q);
          }
        });
      } else {
        addQuestionForObj(_q);
      }
    });

    return Object.keys(qsMap);
  }

  static getAcneUrl(user: string) {
    const answers = this.answers;
    let sku = 0;
    if (answers.states.category === 'Mild') {
      if (
        answers.acneAllergic.yes &&
        (answers.acneAllergicMoreMildDrugs.hydroquinone ||
          answers.acneAllergicMoreMildDrugs.corticosteroids ||
          answers.acneAllergicMoreMildDrugs.tretinoin)
      ) {
        sku = 5;
      } else {
        sku =
          answers.acneSkinGoal.reduce || answers.acneSkinGoal.all
            ? 1
            : answers.acneSkinGoal.fewer
            ? 3
            : answers.acneSkinGoal.getRid
            ? 2
            : answers.acneSkinGoal.fight || answers.acneSkinGoal.clogged
            ? 1
            : 0;
      }
    } else if (answers.states.category === 'Moderate') {
      if (answers.acneAllergic.no) {
        sku =
          answers.acneSkinGoal.getRid || answers.acneSkinGoal.all
            ? 7
            : answers.acneSkinGoal.fewer ||
              answers.acneSkinGoal.reduce ||
              answers.acneSkinGoal.fight ||
              answers.acneSkinGoal.clogged
            ? 6
            : 0;
      } else {
        // acneAllergic: yes
        sku = answers.acneAllergicMoreModerateDrugs.tretinoin
          ? 10
          : answers.acneAllergicMoreModerateDrugs.clindamycin
          ? 8
          : answers.acneAllergicMoreModerateDrugs.erythromycin
          ? 9
          : answers.acneAllergicMoreModerateDrugs.moderateDrugsMore ||
            answers.acneAllergicMoreModerateDrugs.moderateDrugsOther
          ? 5
          : 0;
      }
    } else if (answers.states.category === 'Severe') {
      if (answers.acneAllergic.no) {
        sku =
          answers.acneSkinGoal.getRid ||
          answers.acneSkinGoal.fewer ||
          answers.acneSkinGoal.reduce ||
          answers.acneSkinGoal.fight ||
          answers.acneSkinGoal.clogged ||
          answers.acneSkinGoal.all
            ? 12
            : 0;
      } else {
        // acneAllergic: yes
        sku = answers.acneAllergicMoreSevereDrugs.sulfacetamide ? 5 : 12;
      }
    } else if (answers.states.category === 'Extreme') {
      sku = 12;
    }

    if (
      answers.acnePregnant?.pregnant ||
      answers.acnePregnant?.breastfeeding ||
      answers.acnePregnant?.conceive
    ) {
      if (answers.states.category === 'Mild') {
        sku = 5;
      } else if (answers.states.category === 'Moderate') {
        sku = 10;
      } else if (answers.states.category === 'Severe') {
        sku = 12;
      }
    }

    return `https://jill.health/acne-recommendation-page/?username=${user}&sku=${sku}&st=${
      Object.keys(answers.acneSkinType)[0]
    }&sg=${skinGoalMap[Object.keys(answers.acneSkinGoal)[0]]}&aa=${Object.keys(
      answers.acneBreaking
    ).join(',')}&sensitivity=${Object.keys(answers.acneSensitive)[0]}`;
  }

  static getMessagesUrl() {
    return PATIENT_WEB_PAGES_URLS.messages.inbox;
  }
  static getStrength(name: string): string {
    name = name.toLowerCase();
    if (name.includes('tirzepatide')) {
      return '10';
    } else if (name.includes('semaglutide')) {
      return '2.5';
    } else if (name.includes('mounjaro')) {
      return '2.5';
    } else if (name.includes('zepbound')) {
      return '2.5';
    } else if (name.includes('tadalafil')) {
      return '2.5';
    } else if (name.includes('ozempic')) {
      return '0.25';
    } else if (name.includes('sildenafil')) {
      return '20';
    } else {
      return '0';
    }
  }

  static flattenConsult(consult: IConsultation) {
    const flattenedConsult: IFlattenedQuestion[] = [];
    const supportedTypes: Record<FlattenedQuestionType, boolean> = {
      text: true,
      date: true,
      number: true,
      text_list: true,
      radio: true,
      dropdown: true,
      single_select: true,
      multi_select: true,
      check: true
    };

    const keys = ConsultationUtils.getQuestionStackFromAnswers(
      formTypes[consult.type].questions,
      consult.answers,
      false
    );

    const flattenQuestion = (question, answer, parent): IFlattenedQuestion => {
      const type = question.type as FlattenedQuestionType;

      if (!supportedTypes[type]) {
        throw new Error(`Unsupported question: ${JSON.stringify(question)}`);
      }

      const questionKey =
        question.name || question.field || parent?.field || parent?.name;

      switch (type) {
        case 'text':
        case 'date':
        case 'number':
          return {
            type,
            questionLabel:
              (typeof question.label === 'string'
                ? question.label
                : question.label?.text) || question.placeholder,
            questionKey,
            answer: answer[questionKey],
            extras: question.extras
          };

        case 'text_list':
          return {
            type,
            questionLabel: question.label,
            questionKey,
            answer: (answer.list || []).map(item => item.name),
            extras: question.extras
          };

        case 'radio':
        case 'dropdown':
          return {
            type: 'single_select',
            questionLabel: question.label,
            questionKey,
            answer: answer[questionKey],
            options: question.options.map(option => ({
              label: option.value,
              key: option.key
            })),
            extras: question.extras
          };

        case 'single_select':
          return {
            type: 'single_select',
            questionLabel: question.label,
            questionKey,
            answer: question.options
              .map(option => option.key)
              .find(key => answer[key] === true),
            options: question.options.map(option => ({
              label: option.value,
              key: option.key
            })),
            extras: question.extras
          };

        case 'check':
          return {
            type,
            questionLabel: question.label,
            questionKey,
            answer: !answer.isNo,
            extras: question.extras
          };

        case 'multi_select':
          return {
            type,
            questionLabel: question.label,
            questionKey,
            answer: question.options
              .map(option => option.key)
              .filter(key => answer[key] === true),
            options: question.options.map(option => ({
              label: option.value,
              key: option.key
            })),
            extras: question.extras
          };
      }
    };

    const recursiveFlatten = (question, answer, parent) => {
      if (supportedTypes[question.type]) {
        const flattenedQuestion = flattenQuestion(question, answer, parent);
        flattenedConsult.push(flattenedQuestion);

        if (question.options && isArray(question.options)) {
          question.options
            .filter(option => {
              if (!option.answer) {
                return false;
              }
              const answer = flattenedQuestion.answer;
              if (Array.isArray(answer)) {
                return answer.includes(option.key);
              }
              return option.key === answer;
            })
            .forEach(option => {
              recursiveFlatten(option.answer, answer, question);
            });
        }

        return;
      }

      switch (question.type) {
        case 'list':
          (question.items || []).forEach(item => {
            recursiveFlatten(item, answer, parent);
          });
          break;

        case 'select':
        case 'info':
          if (!question.answer) {
            console.error(
              `Question has no answer: ${JSON.stringify(question)}`
            );
            break;
          }
          recursiveFlatten(
            { ...question, ...question.answer, answer: undefined },
            answer[question.field],
            question
          );
          break;

        case 'polar_check':
          const flattenedQuestion = flattenQuestion(
            {
              label: question.label,
              type: 'check',
              name: question.field
            },
            answer[question.field],
            parent
          );
          flattenedConsult.push(flattenedQuestion);
          if (flattenedQuestion.answer) {
            recursiveFlatten(
              { ...question, ...question.answer, answer: undefined },
              answer[question.field],
              question
            );
          }
          break;

        case 'bmi': {
          const questionLabel =
            (typeof parent?.label === 'string'
              ? parent.label
              : parent?.label?.text) || '';
          const bmiLabel = question.bmiLabel || 'BMI';
          const heightLabel = question.heightLabel || 'Height';
          const weightLabel = question.weightLabel || 'Weight';
          const questionKey = parent?.field || parent?.name;

          if (question.showWeight !== false && answer?.weight != null) {
            flattenedConsult.push({
              type: 'text',
              questionLabel: `${questionLabel} ${weightLabel}`.trim(),
              questionKey,
              answer: `${answer.weight}${
                answer.weightUnit ? ` ${answer.weightUnit}` : ''
              }`
            });
          }

          if (question.showHeight !== false) {
            const height = formatHeight(answer);
            if (height) {
              flattenedConsult.push({
                type: 'text',
                questionLabel: `${questionLabel} ${heightLabel}`.trim(),
                questionKey,
                answer: height
              });
            }
          }

          if (question.showCalculatedValues !== false) {
            const bmi = calculateBMI(answer);
            if (bmi !== null) {
              flattenedConsult.push({
                type: 'number',
                questionLabel: `${questionLabel} ${bmiLabel}`.trim(),
                questionKey,
                answer: bmi
              });
            }
          }
          break;
        }

        default:
          console.error(`Unsupported question type: ${question.type}`);
          break;
      }
    };

    keys.forEach(key => {
      const questionModel = QuestionsCatalog.find(
        q => q.field === key
      ) as IQuestionModel;

      if (!questionModel) {
        return;
      }

      recursiveFlatten(questionModel, consult.answers, null);
    });

    return flattenedConsult;
  }
}
